import { doodModalConfig } from './dood-modal-config';
import { propertyValue } from './dood-config-core';
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from './descriptor-field-order';
import type { IBlockDescriptor } from './descriptor';

/** Serializable preview state for `AuthenticationModalComponent`. */
export type AuthenticationModalScenarioContent = {
  authenticationStep: 'login' | 'signup';
  authenticationSubStep?: 'HOME' | 'EMAIL_LOGIN';
  userJourneyType?: 'ASK_EMAIL_FIRST' | 'ASK_LOGIN_OR_SIGNUP';
};

const SIGNUP_FOOTER_SLOT_ALLOWED_TYPES = [
  'TextBlockComponent',
  'ModalHeaderBlockComponent',
  'ModalButtonBlockComponent',
] as const;

/**
 * Legacy CB1 field order for `AuthenticationModal`.
 * Restores step headings, signup sub-sections, slot and provider blocks.
 */
export const AUTHENTICATION_MODAL_LEGACY_FIELD_ORDER = [
  { type: 'heading', value: 'First step' },
  'first_step_heading',
  'user_journey_type',
  'first_step_login_button',
  'first_step_signup_button',
  { type: 'heading', value: 'Login step' },
  'login_step_heading',
  { type: 'heading', value: 'Signup step' },
  'signup_step_heading',
  'signup_step_subheading',
  { type: 'heading', value: 'Firstname', level: 3 },
  'signup_step_display_firstname',
  'signup_step_firstname_label',
  'signup_step_firstname_help',
  { type: 'heading', value: 'Lastname', level: 3 },
  'signup_step_display_lastname',
  'signup_step_lastname_label',
  'signup_step_lastname_help',
  { type: 'heading', value: 'Email', level: 3 },
  'signup_step_email_label',
  'signup_step_email_help',
  { type: 'heading', value: 'Phone', level: 3 },
  'signup_step_display_phone',
  'signup_step_phone_label',
  'signup_step_phone_help',
  { type: 'heading', value: 'Password', level: 3 },
  'signup_step_password_label',
  'signup_step_password_help',
  { type: 'heading', value: 'Legals', level: 3 },
  'legal_checkbox_label',
  'optin_checkbox_label',
  'signup_footer',
  { type: 'separator' },
  'cgv_url',
  'privacy_policy_url',
  'legal_notice_url',
  { type: 'separator' },
  { type: 'heading', value: 'Authentication provider' },
  'authentication_provider_google',
  'authentication_provider_facebook',
  'authentication_provider_apple',
] as const satisfies readonly DescriptorFieldOrderItem[];

const authenticationModalBuilt = doodModalConfig(
  {
    name: 'Authentication modal',
    handle: 'authentication-modal',
    internal_name: 'AuthenticationModal',
    componentType: 'AuthenticationModalComponent',
    category: 'modal',
    description: 'User authentication modal (login / signup).',
    keywords: ['authentication', 'login', 'signup', 'modal'],
    sharedFields: {},
    defaultHandle: 'authentication-modal',
    isContentModal: true,
    slots: [
      {
        key: 'signup_footer',
        name: 'pages.blocks.authentication-modal.signup-footer.label',
        accept: 'explicitOnly',
        allowedTypes: [...SIGNUP_FOOTER_SLOT_ALLOWED_TYPES],
      },
    ],
  },

  () => ({
    background_color: propertyValue({
      type: 'color',
      value: {"type":"hex","value":"#FFFFFF"},
      options: {
        "label": "Couleur de fond"
      }
    }),
    first_step_heading: propertyValue({
      type: 'text',
      value: 'Connectez-vous ou créez votre compte',
      options: { label: 'Heading', placeholder: 'Connectez-vous ou créez votre compte' },
    }),
    user_journey_type: propertyValue({
      type: 'select',
      value: 'ASK_EMAIL_FIRST',
      options: {
        label: 'Parcours de connexion',
        default: 'ASK_EMAIL_FIRST',
        // CB2 select editor reads `choices` (legacy CB1 used nested `options`)
        choices: [
          { value: 'ASK_EMAIL_FIRST', label: "Demander l'adresse e-mail" },
          { value: 'ASK_LOGIN_OR_SIGNUP', label: 'Choisir entre inscription ou connexion' },
        ],
      },
    }),
    first_step_login_button: propertyValue({
      type: 'text',
      value: 'Se connecter',
      options: { label: 'Login button', placeholder: 'Se connecter' },
    }),
    first_step_signup_button: propertyValue({
      type: 'text',
      value: 'Créer un compte',
      options: { label: 'Signup button', placeholder: 'Créer un compte' },
    }),
    login_step_heading: propertyValue({
      type: 'text',
      value: 'Connexion',
      options: { label: 'Heading', placeholder: 'Connexion' },
    }),
    signup_step_heading: propertyValue({
      type: 'text',
      value: 'Inscription',
      options: { label: 'Heading', placeholder: 'Inscription' },
    }),
    signup_step_subheading: propertyValue({
      type: 'text',
      value: '',
      options: { label: 'Subheading' },
    }),
    signup_step_display_firstname: propertyValue({
      type: 'checkbox',
      value: true,
      options: { label: 'Display firstname input', default: true },
    }),
    signup_step_firstname_label: propertyValue({
      type: 'text',
      value: 'Prénom',
      options: { label: 'Firstname label', placeholder: 'Prénom' },
    }),
    signup_step_firstname_help: propertyValue({
      type: 'text',
      value: '',
      options: { label: 'Firstname help' },
    }),
    signup_step_display_lastname: propertyValue({
      type: 'checkbox',
      value: true,
      options: { label: 'Display lastname input', default: true },
    }),
    signup_step_lastname_label: propertyValue({
      type: 'text',
      value: 'Nom',
      options: { label: 'Lastname label', placeholder: 'Nom' },
    }),
    signup_step_lastname_help: propertyValue({
      type: 'text',
      value: '',
      options: { label: 'Lastname help' },
    }),
    signup_step_email_label: propertyValue({
      type: 'text',
      value: 'Email',
      options: { label: 'Email label', placeholder: 'Email' },
    }),
    signup_step_email_help: propertyValue({
      type: 'text',
      value: '',
      options: { label: 'Email help' },
    }),
    signup_step_display_phone: propertyValue({
      type: 'checkbox',
      value: true,
      options: { label: 'Display phone input', default: true },
    }),
    signup_step_phone_label: propertyValue({
      type: 'text',
      value: 'Téléphone',
      options: { label: 'Phone label', placeholder: 'Téléphone' },
    }),
    signup_step_phone_help: propertyValue({
      type: 'text',
      value: '',
      options: { label: 'Phone help' },
    }),
    signup_step_password_label: propertyValue({
      type: 'text',
      value: 'Mot de passe',
      options: { label: 'Password label', placeholder: 'Mot de passe' },
    }),
    signup_step_password_help: propertyValue({
      type: 'text',
      value: '',
      options: { label: 'Password help' },
    }),
    legal_checkbox_label: propertyValue({
      type: 'wysiwyg',
      value: '',
      options: {
        label: 'Legal checkbox label',
        help: 'Text displayed next to the legal checkbox. Leave blank for default value.',
        minHeight: '75px',
      },
    }),
    optin_checkbox_label: propertyValue({
      type: 'wysiwyg',
      value: '',
      options: {
        label: 'Optin checkbox label',
        help: 'Text displayed next to the optin checkbox. Leave blank for default value.',
        minHeight: '75px',
      },
    }),
    cgv_url: propertyValue({
      type: 'text',
      value: 'https://www.dood.com/corporate/cgv/',
      options: { label: 'editBlock.authenticationModal.cgvUrl', placeholder: 'https://' },
    }),
    privacy_policy_url: propertyValue({
      type: 'text',
      value: 'https://www.dood.com/corporate/politique-de-confidentialite/',
      options: {
        label: 'editBlock.authenticationModal.privacyPolicyUrl',
        placeholder: 'https://',
      },
    }),
    legal_notice_url: propertyValue({
      type: 'text',
      value: 'https://www.dood.com/corporate/mentions-legales/',
      options: {
        label: 'editBlock.authenticationModal.legalNoticeUrl',
        placeholder: 'https://',
      },
    }),
    authentication_provider_google: propertyValue({
      type: 'checkbox',
      value: true,
      options: { label: 'Signup with Google', default: true },
    }),
    authentication_provider_facebook: propertyValue({
      type: 'checkbox',
      value: true,
      options: { label: 'Signup with Facebook', default: true },
    }),
    authentication_provider_apple: propertyValue({
      type: 'checkbox',
      value: false,
      options: {
        label:
          'Signup with Apple — The domain must be explicitly authorized on the Apple Developer console',
        default: false,
      },
    }),
  }),

  () => ({
    tabs: [
      {
        label: 'First step',
        key: 'first_step',
        fieldsets: [
          {
            legend: 'First step',
            fields: ["background_color", 
              'first_step_heading',
              'user_journey_type',
              'first_step_login_button',
              'first_step_signup_button',
            ],
          },
        ],
      },
      {
        label: 'Login',
        key: 'login',
        fieldsets: [
          {
            legend: 'Login step',
            fields: ['login_step_heading'],
          },
        ],
      },
      {
        label: 'Signup',
        key: 'signup',
        fieldsets: [
          {
            legend: 'Signup step',
            fields: ['signup_step_heading', 'signup_step_subheading'],
          },
          {
            legend: 'Firstname',
            fields: [
              'signup_step_display_firstname',
              'signup_step_firstname_label',
              'signup_step_firstname_help',
            ],
          },
          {
            legend: 'Lastname',
            fields: [
              'signup_step_display_lastname',
              'signup_step_lastname_label',
              'signup_step_lastname_help',
            ],
          },
          {
            legend: 'Email',
            fields: ['signup_step_email_label', 'signup_step_email_help'],
          },
          {
            legend: 'Phone',
            fields: [
              'signup_step_display_phone',
              'signup_step_phone_label',
              'signup_step_phone_help',
            ],
          },
          {
            legend: 'Password',
            fields: ['signup_step_password_label', 'signup_step_password_help'],
          },
        ],
      },
      {
        label: 'Legals',
        key: 'legals',
        fieldsets: [
          {
            legend: 'Legals',
            fields: [
              'legal_checkbox_label',
              'optin_checkbox_label',
              'cgv_url',
              'privacy_policy_url',
              'legal_notice_url',
            ],
          },
        ],
      },
      {
        label: 'Providers',
        key: 'providers',
        fieldsets: [
          {
            legend: 'Authentication provider',
            fields: [
              'authentication_provider_google',
              'authentication_provider_facebook',
              'authentication_provider_apple',
            ],
          },
        ],
      },
    ],
  })
);

const _generatedAuthenticationModalDescriptor =
  authenticationModalBuilt.authenticationModalDescriptor;

export function authenticationModalDescriptor(): IBlockDescriptor;
export function authenticationModalDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function authenticationModalDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedAuthenticationModalDescriptor;
  }
  return buildOrderedBlockDescriptor(
    _generatedAuthenticationModalDescriptor,
    authenticationModalBuilt.authenticationModalProperties,
    fieldOrder,
  );
}

export const authenticationModalDefinition = authenticationModalBuilt;

export const authenticationModalResolveConfig =
  authenticationModalBuilt.authenticationModalResolveConfig;

export const authenticationModalConfig = authenticationModalBuilt.authenticationModalConfig;

export const authenticationModalProperties =
  authenticationModalBuilt.authenticationModalProperties;

export type AuthenticationModalConfig = typeof authenticationModalBuilt.config;

export const authenticationModalDefinitionSetupScenarios =
  authenticationModalBuilt.authenticationModalDefinitionSetupScenarios;

export const authenticationModalModalDefinition =
  authenticationModalBuilt.modalDefinition;
export const authenticationModalDashboardLayout =
  authenticationModalBuilt.dashboardLayout;
